import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import ChatWidget from "Templates/Template4/Components/ChatBot/ChatWidget";
import SocialMediaLinks from "./SocialMediaLinks";
import { useEcomConfig } from "hooks/useEcomConfig";
import { useGetBooksCategoryQuery } from "Services/CategoryApiSlice";
import { useGetAllActiveAuthorsQuery } from "Services/Admin/ActiveAuthorApiSlice";
import { STORES } from "Templates/Template4/Pages/StoreLocator";
import { Truck, CreditCard, BookOpen, Zap } from "lucide-react";

const PLANT = process.env.REACT_APP_PLANT || "";

interface Props {
  brandName: string;
}

const Footer: React.FC<Props> = ({ brandName }: Props) => {
  const {
    ecomConfig,
    showAuthor,
    showLanguage,
    showAcademic,
    showMerchandise,
    showSellBooks,
  } = useEcomConfig();

  const { data: categoriesData = [] } = useGetBooksCategoryQuery();
  const { data: authorsData = [] } = useGetAllActiveAuthorsQuery();

  const confidenceFeatures = useMemo(() => {
    let shippingSubLabel = "For orders above ₹500"; // fallback

    if (ecomConfig) {
      if (
        ecomConfig.isShippingChargesApplicable === false ||
        ecomConfig.shippingChargeThresholdAmount === 0
      ) {
        shippingSubLabel = "No delivery charges";
      } else {
        shippingSubLabel = `For orders above ₹${ecomConfig.shippingChargeThresholdAmount}`;
      }
    }

    return [
      {
        icon: <Truck strokeWidth={1.5} />,
        label: "Free Shipping",
        subLabel: shippingSubLabel,
      },
      {
        icon: <CreditCard strokeWidth={1.5} />,
        label: "Multiple Payment Option",
        subLabel: "Secure checkout",
      },
      {
        icon: <BookOpen strokeWidth={1.5} />,
        label: "New & Pre-Owned Books",
        subLabel: "Best collections",
      },
      {
        icon: <Zap strokeWidth={1.5} />,
        label: "Fast & Reliable Delivery",
        subLabel: "Pan India shipping",
      },
    ];
  }, [ecomConfig]);
  return (
    <>
      {/* <ChatWidget /> */}

      <footer className="bg-vintageBg font-gilroyRegular">
        {/* Green confidence banner — full width, no rounded corners */}
        <div
          className="bg-vintageText text-vintageBg px-2 md:px-4 py-8 md:py-10"
          data-aos="fade-up"
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-4 max-w-6xl mx-auto">
            {confidenceFeatures.map((feature, index) => (
              <div key={index} className="flex flex-col md:flex-row items-center md:items-start text-center md:text-left gap-4">
                <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-full bg-vintageBg/10 flex items-center justify-center border border-vintageBg/20 shadow-inner group transition-all duration-300 hover:scale-110">
                  {React.cloneElement(feature.icon as React.ReactElement, { 
                    className: "w-6 h-6 md:w-8 md:h-8 text-vintageBg opacity-90 group-hover:opacity-100" 
                  })}
                </div>
                <div className="flex flex-col justify-center h-full">
                  <h3 className="font-gilroyRegular text-sm md:text-base font-bold tracking-wide leading-tight">
                    {feature.label}
                  </h3>
                  <p className="text-[10px] md:text-xs font-medium tracking-wider opacity-60 mt-0.5">
                    {feature.subLabel}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="px-2 md:px-4 py-8 md:py-10">
          <div className="flex flex-col lg:flex-row">
            <div className="w-full lg:w-2/6 md:pr-12" data-aos="flip-up">
              <Link to="/">
                <img
                  src="/Moore_market_img.png"
                  alt="Moore Market"
                  className="h-16 w-auto mx-auto md:mx-0 rounded"
                />
              </Link>
              <p className="mt-4 text-sm text-center md:text-start leading-relaxed text-gray-600 text-justify">
                Ever wanted to buy a book but could not because it was too
                expensive? Worry not! Moore Market is here. These days, it's
                being called the Robinhood of the world of books.
              </p>
            </div>
            <div className="w-full lg:w-4/6 flex-1 grid grid-cols-2 md:grid-cols-4 gap-x-1 md:gap-x-2 gap-y-12 py-6 lg:my-0">
              <div data-aos="flip-up">
                <h3 className="font-bold text-base md:text-lg text-vintageText tracking-wider mb-4">
                  Shop
                </h3>
                <ul className="space-y-2">
                  <li>
                    <Link
                      to="/collections"
                      className="text-xs lg:text-sm text-gray-800 tracking-wider hover:text-vintageText transition-colors duration-300"
                    >
                      Category
                    </Link>
                  </li>
                  {showAuthor && (
                    <li>
                      <Link
                        to="/authors"
                        className="text-xs lg:text-sm text-gray-800 tracking-wider hover:text-vintageText transition-colors duration-300"
                      >
                        Author
                      </Link>
                    </li>
                  )}

                  {showLanguage && (
                    <li>
                      <Link
                        to="/language"
                        className="text-xs lg:text-sm text-gray-800 tracking-wider hover:text-vintageText transition-colors duration-300"
                      >
                        Language
                      </Link>
                    </li>
                  )}

                  {showAcademic && (
                    <li>
                      <Link
                        to="/academic"
                        className="text-xs lg:text-sm text-gray-800 tracking-wider hover:text-vintageText transition-colors duration-300"
                      >
                        Academic
                      </Link>
                    </li>
                  )}

                  {showMerchandise && (
                    <li>
                      <Link
                        to="/merchandise"
                        className="text-xs lg:text-sm text-gray-800 tracking-wider hover:text-vintageText transition-colors duration-300"
                      >
                        Merchandise
                      </Link>
                    </li>
                  )}
                </ul>
              </div>

              <div data-aos="flip-up">
                <h3 className="font-bold text-base md:text-lg text-vintageText tracking-wider mb-4">
                  Our Links
                </h3>
                <ul className="space-y-2">
                  <li>
                    <Link
                      to="/about-us"
                      className="text-xs lg:text-sm text-gray-800 tracking-wider hover:text-vintageText transition-colors duration-300"
                    >
                      About Us
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/contact-us"
                      className="text-xs lg:text-sm text-gray-800 tracking-wider hover:text-vintageText transition-colors duration-300"
                    >
                      Contact Us
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/faq"
                      className="text-xs lg:text-sm text-gray-800 tracking-wider hover:text-vintageText transition-colors duration-300"
                    >
                      FAQs
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/blogs"
                      className="text-xs lg:text-sm text-gray-800 tracking-wider hover:text-vintageText transition-colors duration-300"
                    >
                      Blogs
                    </Link>
                  </li>
                  {showSellBooks && (
                    <li>
                      <Link
                        to="/sell-with-us"
                        className="text-xs lg:text-sm text-gray-800 tracking-wider hover:text-vintageText transition-colors duration-300"
                      >
                        Sell with Us
                      </Link>
                    </li>
                  )}
                  <li>
                    <Link
                      to="/track-order"
                      className="text-xs lg:text-sm text-gray-800 tracking-wider hover:text-vintageText transition-colors duration-300"
                    >
                      Track Order
                    </Link>
                  </li>
                </ul>
              </div>

              <div data-aos="flip-up">
                <h3 className="font-bold text-base md:text-lg text-vintageText tracking-wider mb-4">
                  Quick Links
                </h3>
                <ul className="space-y-2">
                  <li>
                    <Link
                      to="/cancellation-and-refund-policy"
                      className="text-xs lg:text-sm text-gray-800 tracking-wider hover:text-vintageText transition-colors duration-300"
                    >
                      Cancellation & Refund policy
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/return-policy"
                      className="text-xs lg:text-sm text-gray-800 tracking-wider hover:text-vintageText transition-colors duration-300"
                    >
                      Return Policy
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/shipping-policy"
                      className="text-xs lg:text-sm text-gray-800 tracking-wider hover:text-vintageText transition-colors duration-300"
                    >
                      Shipping Policy
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/terms-and-conditions"
                      className="text-xs lg:text-sm text-gray-800 tracking-wider hover:text-vintageText transition-colors duration-300"
                    >
                      Terms & Conditions
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/privacy-policy"
                      className="text-xs lg:text-sm text-gray-800 tracking-wider hover:text-vintageText transition-colors duration-300"
                    >
                      Privacy Policy
                    </Link>
                  </li>
                </ul>
              </div>

              <div data-aos="flip-up">
                <h3 className="font-bold text-base md:text-lg text-vintageText tracking-wider mb-4">
                  Support
                </h3>
                <ul className="space-y-2 flex flex-col justify-start items-start">
                  <li className="text-xs lg:text-sm text-gray-800 hover:text-vintageText transition-colors duration-300 flex gap-1 items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-phone-icon lucide-phone"
                    >
                      <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" />
                    </svg>
                    <a
                      href="tel:+916374410039"
                      className="underline"
                      // style={{ color: "#326638" }}
                    >
                      +91 63744 10039
                    </a>
                  </li>
                  <li className="text-xs lg:text-sm text-gray-800 hover:text-vintageText transition-colors duration-300 flex gap-1 items-center justify-center">
                    <img
                      src="/social/whatsapp.svg"
                      alt="Whatsapp icon"
                      className="h-4 w-auto mx-auto md:mx-0 rounded"
                    />
                    <a
                      href="tel:+916374410039"
                      className="underline"
                      // style={{ color: "#326638" }}
                    >
                      +91 63744 10039
                    </a>
                  </li>
                  <li className="text-xs lg:text-sm text-gray-800 hover:text-vintageText transition-colors duration-300 flex gap-1 items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-mail-icon lucide-mail hidden"
                    >
                      <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" />
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                    </svg>
                    <a
                      href="mailto:support@mooremarket.in"
                      className="underline"
                      // style={{ color: "#326638" }}
                    >
                      support@mooremarket.in
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="border-t border-vintageText border-opacity-20 flex flex-col md:flex-row justify-between items-center gap-2 pt-6">
            <p className="order-2 md:order-1 text-gray-600 tracking-wider text-sm md:text-xs text-center md:text-start">
              © 2026{" "}
              <span className="font-bold text-vintageText">
                Moore Market Private Limited
              </span>
              . All rights reserved. Powered by Alphabit Technologies IND.
            </p>
            <SocialMediaLinks />
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
